import { useCallback, useEffect, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { motion } from 'framer-motion';

import {
  containerStyle,
  likeContainerStyle
} from '@/components/Likes/styles.ts';
import LikesListSkeleton from '@/components/Likes/LikesListSkeleton/LikesListSkeleton.tsx';
import { useSummariesStore } from '@/store/useSummariesStore.ts';
import { useProfileStore } from '@/store/useProfileStore.ts';
import { LIKES_LIMIT, useLikesListStore } from '@/store/useLikesListStore.ts';
import { catchErrors } from '@/utils/sentry-monitor.tsx';
import { useDiscoveryStore } from '@/store/useDiscoveryStore.ts';
import Modal from '@/animations/Modal.tsx';
import PaywallPopup from '@/components/Popup/PaywallPopup/PaywallPopup.tsx';
import {
  Gender,
  PaywallTypes,
  ProductInvoicePlacements,
  VideoState
} from '@/enums/apiEnums.ts';
import EmptyLikesList from '@/components/Likes/EmptyLikesList.tsx';
import LikeCard from '@/components/Likes/LikeCard/LikeCard.tsx';
import MatchMakerService from '@/api/matchmaker/matchmaker.ts';
import UserProfilePopup from '@/components/Popup/UserProfile/UserProfilePopup.tsx';
import { routes } from '@/navigation/routes.tsx';
import { useNavigate } from 'react-router-dom';
import LikesListBanner from '@/components/Likes/HeaderBanners/LikesListBanner.tsx';

export const MAX_NAME_LENGTH = 11;

const ROW_HEIGHT = 220;
const ROW_GAP = 15;
const COLUMNS = 2;
const EXIT_DURATION = 0.3;

const LikesList = () => {
  const navigate = useNavigate();

  const [payWallOpen, setPayWallOpen] = useState<boolean>(false);

  const closePayWall = () => setPayWallOpen(false);
  const openPayWall = () => setPayWallOpen(true);

  const [userProfileOpened, setUserProfileOpened] = useState<boolean>(false);
  const [userProfileHid, setUserProfileHid] = useState<string | undefined>(
    undefined
  );

  const closeUserProfile = () => {
    setUserProfileOpened(false);
    setUserProfileHid(undefined);
  };
  const openUserProfile = (userHid: string) => {
    setUserProfileHid(userHid);
    setUserProfileOpened(true);
  };

  const { getLikesCount, likesCount } = useSummariesStore();
  const {
    likes,
    allLikesLoaded,
    loadLikes,
    like,
    pageToken,
    isFetching,
    reset
  } = useLikesListStore();
  const { premium, aboutMe } = useProfileStore();
  const { filterCandidates } = useDiscoveryStore();

  const isFemale = aboutMe?.profile?.gender === Gender.FEMALE;
  const hasVideo =
    (aboutMe?.profile?.v2videos?.length ?? 0) > 0 &&
    !aboutMe?.profile?.v2videos?.[0].in_review &&
    aboutMe?.profile?.v2videos?.[0].state === VideoState.STATE_ACTIVE;
  const isLocked = isFemale
    ? premium.isExpired && !hasVideo
    : premium.isExpired;

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [removingHids, setRemovingHids] = useState<Set<string>>(new Set());

  const rowCount = Math.ceil(likes.length / COLUMNS);

  const virtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => ROW_HEIGHT + ROW_GAP,
    overscan: 3
  });

  const likeUser = async (hid: string) => {
    try {
      setRemovingHids((prev) => new Set(prev).add(hid));

      await MatchMakerService.likeMatchCandidate(hid);
      filterCandidates(hid);

      setTimeout(() => {
        like(hid);
        setRemovingHids((prev) => {
          const next = new Set(prev);
          next.delete(hid);
          return next;
        });
      }, EXIT_DURATION * 1000);

      await getLikesCount();
    } catch (e) {
      setRemovingHids((prev) => {
        const next = new Set(prev);
        next.delete(hid);
        return next;
      });
      catchErrors(e, true);
    }
  };

  useEffect(() => {
    void loadLikes();

    return () => {
      reset();
    };
  }, []);

  const triggerItemIndex = likes.length - Math.floor(LIKES_LIMIT / 2);
  const triggerRowIndex = Math.floor(triggerItemIndex / COLUMNS);

  const handleLoadMore = useCallback(() => {
    if (!isFetching && !isLocked) {
      void loadLikes(pageToken);
    }
  }, [isFetching, isLocked, pageToken, loadLikes]);

  const virtualRows = virtualizer.getVirtualItems();

  useEffect(() => {
    if (virtualRows.length === 0) return;

    const lastVisibleRow = virtualRows[virtualRows.length - 1];
    if (
      lastVisibleRow &&
      lastVisibleRow.index >= triggerRowIndex &&
      !allLikesLoaded
    ) {
      handleLoadMore();
    }
  }, [virtualRows, triggerRowIndex, allLikesLoaded, handleLoadMore]);

  const isLoading = likes.length === 0 && !allLikesLoaded;
  const hasLikes = likesCount !== undefined && likesCount > 0;

  const showLikesListBanner = isLocked && hasLikes;

  const handleBannerClick = () => {
    const isMale = aboutMe?.profile.gender === Gender.MALE;

    if (isMale) {
      setPayWallOpen(true);
      return;
    }

    navigate(routes.profileEdit);
  };

  return (
    <>
      {hasLikes ? (
        <div
          ref={scrollContainerRef}
          style={{
            ...containerStyle(isLoading),
            overflow: isLoading ? 'hidden' : 'auto',
            flexWrap: undefined,
            display: 'block'
          }}
        >
          {showLikesListBanner && (
            <LikesListBanner
              gender={aboutMe?.profile.gender as Gender}
              onClick={handleBannerClick}
            />
          )}
          {isLoading && <LikesListSkeleton />}
          {!isLoading && (
            <div
              style={{
                height: virtualizer.getTotalSize(),
                width: '100%',
                position: 'relative'
              }}
            >
              {virtualRows.map((virtualRow) => {
                const rowStartIndex = virtualRow.index * COLUMNS;

                return (
                  <div
                    key={virtualRow.key}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: ROW_HEIGHT,
                      transform: `translateY(${virtualRow.start}px)`,
                      display: 'flex',
                      gap: `${ROW_GAP}px`
                    }}
                  >
                    {Array.from({ length: COLUMNS }).map((_, colIndex) => {
                      const itemIndex = rowStartIndex + colIndex;
                      const item = likes[itemIndex];
                      if (!item) return null;

                      const isRemoving = removingHids.has(item.profile.hid);

                      return (
                        <motion.div
                          key={item.profile.hid}
                          animate={
                            isRemoving
                              ? { opacity: 0, scale: 0.8 }
                              : { opacity: 1, scale: 1 }
                          }
                          transition={{
                            duration: isRemoving ? EXIT_DURATION : 0.2
                          }}
                          style={{
                            ...likeContainerStyle,
                            pointerEvents: isRemoving ? 'none' : 'auto'
                          }}
                        >
                          <LikeCard
                            imgUrl={item.profile.v2photos[0]}
                            name={item.profile.name}
                            age={item.profile.age}
                            onClick={() =>
                              isLocked
                                ? openPayWall()
                                : openUserProfile(item.profile.hid)
                            }
                            onLike={() =>
                              isLocked
                                ? openPayWall()
                                : likeUser(item.profile.hid)
                            }
                            isBlured={isLocked}
                          />
                        </motion.div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        <EmptyLikesList />
      )}
      <Modal
        fullScreen
        scrollable
        open={payWallOpen}
        handleClose={closePayWall}
      >
        <PaywallPopup
          handleClose={closePayWall}
          type={PaywallTypes.TYPE_SUBSCRIPTION}
          placement={
            ProductInvoicePlacements.LIKED_YOU_HIDDEN_LIKES_GET_PREMIUM
          }
          prevHeaderTemplate="light"
        />
      </Modal>

      <Modal fullScreen open={userProfileOpened} handleClose={closeUserProfile}>
        <UserProfilePopup
          handleClose={closeUserProfile}
          userHid={userProfileHid}
          prevHeaderColors="light"
        />
      </Modal>
    </>
  );
};

export default LikesList;
